import React, { useMemo } from 'react';
import { faRefresh, faDownload, faPrint } from '@fortawesome/free-solid-svg-icons';
import ActionToolbar from '../common/ActionToolbar';
import { UI_COLORS } from '../../utils/colors';
import { useTranslation } from 'react-i18next';

/**
 * PurchaseChallanActions
 * Actions toolbar for Purchase Challans: search, refresh, date filters, and export.
 */
const PurchaseChallanActions = ({
    showSearch = true,
    searchValue = '',
    onSearchChange,
    onRefresh,
    loading = false,
    refreshLoading = false,
    onPrint,
    printLoading = false,
    // Filters
    fromDate = '',
    toDate = '',
    onFromDateChange,
    onToDateChange,
    onExport,
    exportLoading = false,
    canExport = false,
}) => {
    const { t } = useTranslation();

    const actions = useMemo(() => {
        const list = [];
        if (onPrint) {
            list.push({
                key: 'print-table',
                label: t('commonCols.print'),
                icon: faPrint,
                onClick: onPrint,
                disabled: printLoading,
                variant: 'ghost',
                showLabelBreakpoint: 'sm',
                spinner: printLoading,
            });
        }
        if (canExport) {
            list.push({
                key: 'export-challans',
                label: t('commonCols.export'),
                icon: faDownload,
                onClick: onExport,
                disabled: exportLoading,
                variant: 'success',
                showLabelBreakpoint: 'sm',
                spinner: exportLoading,
            });
        }
        list.push({
            key: 'refresh-challans',
            label: t('common.buttons.refresh'),
            icon: faRefresh,
            onClick: onRefresh,
            disabled: refreshLoading,
            variant: 'ghost',
            showLabelBreakpoint: 'sm',
            spinner: refreshLoading,
        });
        return list;
    }, [canExport, onRefresh, refreshLoading, onExport, exportLoading, onPrint, printLoading, t]);

    const filters = useMemo(() => {
        const dateInputStyle = {
            backgroundColor: UI_COLORS.FORMS.INPUT.background,
            borderColor: UI_COLORS.FORMS.INPUT.border,
            color: UI_COLORS.TEXT.BLACK || UI_COLORS.FORMS.INPUT.text,
        };
        return [
            <div key="from-date" className="flex-shrink-0 flex items-center gap-2">
                <label className="text-sm" style={{ color: UI_COLORS.FORMS.LABEL }}>From</label>
                <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => onFromDateChange && onFromDateChange(e.target.value)}
                    className="px-3 py-2 rounded-xl border-2"
                    style={dateInputStyle}
                />
            </div>,
            <div key="to-date" className="flex-shrink-0 flex items-center gap-2">
                <label className="text-sm" style={{ color: UI_COLORS.FORMS.LABEL }}>To</label>
                <input
                    type="date"
                    value={toDate}
                    onChange={(e) => onToDateChange && onToDateChange(e.target.value)}
                    className="px-3 py-2 rounded-xl border-2"
                    style={dateInputStyle}
                />
            </div>,
        ];
    }, [fromDate, toDate, onFromDateChange, onToDateChange]);

    return (
        <ActionToolbar
            showSearch={showSearch}
            searchValue={searchValue}
            onSearchChange={onSearchChange}
            searchPlaceholder={t('purchaseChallan.actions.searchPlaceholder')}
            filters={filters}
            actions={actions}
        />
    );
};

export default PurchaseChallanActions;
